import React from "react";

/**
 * App-wide error boundary.
 *
 * Without this, ANY uncaught error thrown while rendering ANY component —
 * a null field, an unexpected API shape, a bad date, etc. — unmounts the
 * entire React tree and leaves the browser showing a fully blank white
 * page, with zero indication of what happened. This is what was happening
 * when opening a customer's 360° profile: the data arrived from the API
 * correctly (visible in the Network tab), but something in that render
 * path threw, and with no boundary anywhere in the app, the crash took
 * down the whole page instead of just that one panel.
 *
 * This boundary catches render errors anywhere below it, logs the real
 * error to the console for diagnosis, and shows a recoverable message
 * instead of a blank screen — with a button to retry without a full page
 * reload where possible, and a reload fallback.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("Render error caught by ErrorBoundary:", error, info?.componentStack);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-[50vh] flex items-center justify-center p-8">
          <div className="max-w-md text-center bg-white border border-line p-8">
            <div className="font-display text-2xl text-maroon mb-2">Something went wrong</div>
            <p className="text-sm text-ash mb-1">
              This section hit an unexpected error while loading — the rest of the site is unaffected.
            </p>
            {this.state.error?.message && (
              <p className="text-xs text-ash/70 font-mono mt-2 mb-4 break-words">{this.state.error.message}</p>
            )}
            <div className="flex gap-3 justify-center mt-4">
              <button onClick={this.handleRetry} className="btn-maroon" data-testid="error-boundary-retry">
                Try Again
              </button>
              <button onClick={() => window.location.reload()} className="btn-outline" data-testid="error-boundary-reload">
                Reload Page
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
